import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUser, faBars, faTimes, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { useCart, useAuth } from '../../context';
import logo from '../../assets/logo.svg';
import './Navbar.css';

const Navbar = () => {
    const { user } = useAuth();
    const { cart } = useCart();
    const navigate = useNavigate();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isCartAnimating, setIsCartAnimating] = useState(false);

    const items = Array.isArray(cart) ? cart : [];
    const cartItemsCount = items.reduce((total, item) => total + (item?.quantity || 0), 0);

    useEffect(() => {
        if (cartItemsCount > 0) {
            setIsCartAnimating(true);
            const timer = setTimeout(() => setIsCartAnimating(false), 500);
            return () => clearTimeout(timer);
        }
    }, [cartItemsCount]);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        // Lock scroll when mobile menu or search is open
        document.body.style.overflow = (isMenuOpen || isSearchOpen) ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isMenuOpen, isSearchOpen]);

    const handleSearchToggle = () => {
        setIsSearchOpen(!isSearchOpen);
        if (isMenuOpen) setIsMenuOpen(false);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/shop?q=${encodeURIComponent(searchQuery.trim())}`);
            setIsSearchOpen(false);
            setSearchQuery('');
        }
    };

    return (
        <>
            <div className="announcement-bar">
                Free Shipping on Orders Over $100 | Join Our Newsletter for 10% Off
            </div>

            {isMenuOpen && <div className="backdrop visible" onClick={() => setIsMenuOpen(false)} />}
            {isSearchOpen && <div className="search-overlay visible" onClick={() => setIsSearchOpen(false)}>
                <div className="search-container" onClick={e => e.stopPropagation()}>
                    <form onSubmit={handleSearchSubmit}>
                        <input 
                            type="text" 
                            placeholder="Search for products..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                        <button type="submit" aria-label="Submit search">
                            <FontAwesomeIcon icon={faSearch} />
                        </button>
                    </form>
                    <button className="close-search" onClick={() => setIsSearchOpen(false)} aria-label="Close search">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
            </div>}

            <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`} aria-label="Primary">
                <button
                    type="button"
                    className="mobile-menu-button"
                    aria-expanded={isMenuOpen}
                    aria-controls="primary-navigation"
                    aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                    onClick={() => {
                        setIsMenuOpen(!isMenuOpen);
                        if (isSearchOpen) setIsSearchOpen(false);
                    }}
                >
                    <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
                </button>

                <div className="logo">
                    <Link to="/" onClick={() => { setIsMenuOpen(false); setIsSearchOpen(false); }}>
                        <img src={logo} alt="Epoxy Garden Logo" className="logo-img" />
                        <h1>Epoxy Garden</h1>
                    </Link>
                </div>

                <div id="primary-navigation" className={`nav-links ${isMenuOpen ? 'active' : ''}`} role="navigation">
                    <NavLink to="/" end onClick={() => setIsMenuOpen(false)}>Home</NavLink>
                    <NavLink to="/shop" onClick={() => setIsMenuOpen(false)}>Shop</NavLink>
                    <NavLink to="/contact" onClick={() => setIsMenuOpen(false)}>Contact Us</NavLink>
                    <NavLink to="/about" onClick={() => setIsMenuOpen(false)}>About</NavLink>
                </div>

                <div className="nav-icons">
                    <button type="button" aria-label="Search" className="icon-button" onClick={handleSearchToggle}>
                        <FontAwesomeIcon icon={faSearch} />
                    </button>
                    <Link to={user ? '/account' : '/login'} aria-label={user ? 'Account' : 'Login'} className="nav-user-link">
                        {user?.image_url ? (
                            <img src={user.image_url} alt="" className="nav-user-avatar" />
                        ) : (
                            <FontAwesomeIcon icon={faUser} />
                        )}
                    </Link>
                    <Link to="/cart" className={`cart-icon ${isCartAnimating ? 'bump' : ''}`} aria-label="Cart">
                        <FontAwesomeIcon icon={faShoppingCart} />
                        {cartItemsCount > 0 && <span className="cart-count">{cartItemsCount}</span>}
                    </Link>
                </div>

            </nav>
        </>
    );
};

export default Navbar;
